// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app/routes/set-username.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app/routes/set-username.tsx");
  import.meta.hot.lastModified = "1747582430551.2563";
}
// REMIX HMR END

import { redirect } from '@remix-run/cloudflare';
import { useLoaderData, useSearchParams } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { initAuthEnvironment } from '~/services/auth.server';
import { ACCESS_AUTHENTICATED_USER_EMAIL_HEADER } from '~/utils/constants';
import getUsername from '~/utils/getUsername.server';

// Add types for Google Identity Services

export const loader = async ({
  request,
  context
}) => {
  // Initialize auth environment
  initAuthEnvironment(context);

  // Check if the user is already authenticated
  const url = new URL(request.url);
  const returnUrl = url.searchParams.get('return-url') ?? '/';

  // If already authenticated via Access header, redirect back
  const accessUsername = request.headers.get(ACCESS_AUTHENTICATED_USER_EMAIL_HEADER);
  if (accessUsername) throw redirect(returnUrl);

  // If already authenticated via Google, redirect back
  const username = await getUsername(request, context);
  if (username) throw redirect(returnUrl);

  // Log Google Client ID for debugging
  console.log('Google Client ID in loader:', {
    clientId: context.env.GOOGLE_CLIENT_ID,
    hasId: !!context.env.GOOGLE_CLIENT_ID
  });

  // Pass client ID directly from environment
  return {
    returnUrl,
    googleClientId: context.env.GOOGLE_CLIENT_ID
  };
};
export const action = async ({
  request: _request,
  context: _context
}) => {
  // Initialize auth environment
  initAuthEnvironment(_context);

  // This action is kept for backward compatibility
  // It will be executed if someone submits the form instead of using Google auth
  return redirect('/auth/google');
};
export default function Login() {
  _s();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return-url') ?? '/';
  const error = searchParams.get('error');
  const {
    googleClientId,
    returnUrl: _loaderReturnUrl
  } = useLoaderData();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [buttonRendered, setButtonRendered] = useState(false);

  // Log client ID for debugging
  console.log('Google Client ID in component:', {
    clientId: googleClientId,
    hasId: !!googleClientId
  });

  // Function to get the appropriate error message based on error type
  const getErrorMessage = error => {
    if (!error) return null;
    switch (error) {
      case 'auth_failed':
        return 'Authentication failed. Please try again.';
      case 'access_denied':
        return 'Access was denied. Please try again or contact support.';
      case 'invalid_token':
        return 'Invalid or expired authentication token. Please try again.';
      case 'server_error':
        return 'A server error occurred. Please try again later.';
      default:
        return 'An error occurred during sign in. Please try again.';
    }
  };
  const errorMessage = getErrorMessage(error) || (!googleClientId ? 'Google Sign-In is not configured properly. Please contact the administrator.' : null);

  // Load Google Identity Services script directly in the component
  useEffect(() => {
    if (!googleClientId || scriptLoaded) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Identity Services script loaded');
      setScriptLoaded(true);
    };
    script.onerror = e => {
      console.error('Error loading Google Identity Services script:', e);
    };
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [googleClientId, scriptLoaded]);

  // Initialize Google Identity Services
  useEffect(() => {
    if (!scriptLoaded || !googleClientId || !window.google?.accounts) {
      console.log('Cannot initialize Google Sign-In: Script not loaded or missing dependencies', {
        scriptLoaded,
        hasClientId: !!googleClientId,
        hasGoogleAccounts: !!window.google?.accounts
      });
      return;
    }
    console.log('Initializing Google Sign-In');
    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: response => {
          // Handle the ID token
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = `/auth/google?returnTo=${encodeURIComponent(returnUrl)}`;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'credential';
          input.value = response.credential;
          form.appendChild(input);
          document.body.appendChild(form);
          form.submit();
        },
        auto_select: true,
        context: 'signin'
      });

      // Render the button
      const signinButton = document.getElementById('g_id_signin');
      if (signinButton) {
        console.log('Rendering Google Sign-In button in header');
        window.google.accounts.id.renderButton(signinButton, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          logo_alignment: 'left',
          width: 250
        });
        setButtonRendered(true);
      } else {
        console.warn("Could not find header button element with id 'g_id_signin'");
      }
      const signinLargeButton = document.getElementById('g_id_signin_large');
      if (signinLargeButton) {
        console.log('Rendering large Google Sign-In button in footer');
        window.google.accounts.id.renderButton(signinLargeButton, {
          type: 'standard',
          theme: 'filled_blue',
          size: 'xlarge',
          text: 'signup_with',
          shape: 'pill',
          logo_alignment: 'center',
          width: 300,
          longtitle: true
        });
      } else {
        console.warn("Could not find footer button element with id 'g_id_signin_large'");
      }

      // Also display the One Tap dialog
      window.google.accounts.id.prompt();
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
    }
  }, [returnUrl, googleClientId, scriptLoaded]);

  // If Google client ID is missing, show a fallback sign-in button
  const renderFallbackButton = () => {
    if (!googleClientId || !buttonRendered) {
      return <a href={`/auth/google?returnTo=${encodeURIComponent(returnUrl)}`} className="flex items-center justify-center gap-2 bg-white text-gray-700 hover:bg-gray-100 rounded-full w-full h-full px-8 py-3 font-medium transition-colors shadow-md">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" role="img" aria-labelledby="google-icon">
						<title id="google-icon">Google Logo</title>
						<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
						<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
						<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"></path>
						<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
					</svg>
					Sign in with Google
				</a>;
    }
    return null;
  };
  return <section className="relative isolate min-h-[80vh] md:min-h-[90vh] w-screen max-w-full overflow-x-hidden bg-[#618A97]">
			{/* Background Video */}
			<video autoPlay loop muted playsInline className="absolute inset-0 -z-10 h-full w-full select-none object-contain md:object-cover object-bottom">
				<source src="/clamo.mp4" type="video/mp4" />
			</video>

			{/* Darken / Vignette */}
			<div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/10 via-transparent to-black/30"></div>

			{/* Header */}
			<header className="flex items-center justify-between px-6 py-4 bg-transparent text-white relative z-10 w-full">
				<a href="/" className="flex items-center">
					<img src="/clamo_logo.svg" alt="Clamo.ai" className="h-8 w-auto" />
				</a>
				<nav className="hidden space-x-6 md:flex">
					<a href="/" className="transition hover:text-[#9be7ff]">
						Product
					</a>
					<a href="/" className="transition hover:text-[#9be7ff]">
						Integrations
					</a>
					<a href="/" className="transition hover:text-[#9be7ff]">
						Pricing
					</a>
					<a href="/" className="transition hover:text-[#9be7ff]">
						Playground
					</a>
					<a href="/" className="transition hover:text-[#9be7ff]">
						Docs
					</a>
				</nav>
				<div className="min-w-[250px] min-h-[46px] rounded-full overflow-hidden">
					{googleClientId ? <div id="g_id_signin"></div> : renderFallbackButton()}
				</div>
			</header>

			{/* Error Message */}
			{errorMessage && <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 w-full max-w-md">
					<div className="mx-4 p-3 bg-red-100 text-red-700 rounded-md border border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700">
						{errorMessage}
					</div>
				</div>}

			{/* Caption Wrapper */}
			<div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col items-center text-center pb-5 w-full">
				<h2 className="mb-8 max-w-3xl px-6 text-4xl font-extrabold leading-tight text-white drop-shadow md:text-6xl">
					Meetings made easy.
				</h2>
				<p className="mb-5 max-w-xl px-4 text-lg text-white/80 md:text-xl">
					Clamo brings teams closer together, making every conversation
					meaningful.
				</p>
				<div className="mb-10 max-w-xl px-4 text-lg text-white/80 md:text-xl text-left mx-auto">
					<div className="flex items-center mb-2">
						<span className="mr-2">✅</span>
						<span>Secure end-to-end communication</span>
					</div>
					<div className="flex items-center mb-2">
						<span className="mr-2">✅</span>
						<span>Only the features you need</span>
					</div>
					<div className="flex items-center mb-2">
						<span className="mr-2">✅</span>
						<span>Simple, intuitive interface</span>
					</div>
				</div>
				<div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 px-4">
					<div className="min-w-[300px] min-h-[40px] flex items-center">
						{googleClientId ? <div id="g_id_signin_large"></div> : renderFallbackButton()}
					</div>
					<button className="rounded-full border border-white/60 bg-transparent text-white hover:bg-white/10 px-8 h-[42px] flex items-center justify-center font-medium transition-colors shadow-md">
						Watch Demo
					</button>
				</div>
			</div>
		</section>;
}
_s(Login, "jyzsxzKLnQIxbr0kifwoTZRHipM=", false, function () {
  return [useSearchParams, useLoaderData];
});
_c = Login;
var _c;
$RefreshReg$(_c, "Login");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;